import React, { useState, useRef, useImperativeHandle } from 'react';
import { message, Spin } from 'antd';
import FormPrerequisites from '../FormPrerequisites/FormPrerequisites'; 
import FormSaveCancelToolbar from '../FormSaveCancelToolbar/FormSaveCancelToolbar'; 
import useCreatePrerequisiteMutation from '../../gql/hooks/useCreatePrerequisiteMutation';
import usePatchPrerequisiteMutation from '../../gql/hooks/usePatchPrerequisiteMutation';
import useDeletePrerequisiteMutation from '../../gql/hooks/useDeletePrerequisiteMutation';

const TabPrerequisites = ( { form, type, isSavingCallback, ref } ) => {
    const formPrerequisitesRef = useRef( useRef );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ isModified, setIsModified ] = useState( false );
    const [ patchPrerequisite ] = usePatchPrerequisiteMutation();
    const [ createPrerequisite ] = useCreatePrerequisiteMutation();
    const [ deletePrerequisite ] = useDeletePrerequisiteMutation();
    const [ openConfirm, setOpenConfirm ] = useState( false );

    useImperativeHandle( ref, () => ( {
        isModified() {
            return isModified;
        },
        showSaveConfirm() {
            setOpenConfirm( true );
        }
    } ) );

    const handleSave = async () => {
        setIsSaving( true );
        setOpenConfirm( false ); 
        isSavingCallback( true ); 

        let hasError = false;
        
        const allOriginal  = form.getFieldValue( 'prerequistieAll' );
        const allModified  = form.getFieldValue( 'prerequisiteAllModified' );
        const andValue = allOriginal !== allModified ? allModified : allOriginal;
       
        const prereqIdsModified  = form.getFieldValue( 'prerequistieIdsModified' );
        
        // Input variables for create and patch mutations
        const getMutationInput = () => {
            return {
                objectId: form.getFieldValue( 'id' ), 
                type: type === 'courses' ? 'COURSE' : 'CURRICULUM',
                operator: andValue ? 'AND' : 'OR',
                prerequisites: prereqIdsModified.map( ( i, index ) => ( { 
                    objectId: i,
                    weight: index
                } ) )
            };
        };

        const prerequisiteSet = form.getFieldValue( 'prerequisiteSet' ) === null 
            ? undefined 
            : form.getFieldValue( 'prerequisiteSet' );
        const prerequisiteIds = form.getFieldValue( 'prerequistieIdsModified' );

        /**************************************
         * Create a new prerequisiteSet 
         * if no previous one exists.
         **************************************/

        if ( !prerequisiteSet &&  prerequisiteIds?.length > 0 ) {
            try {
                const response = await createPrerequisite( { variables: { input: getMutationInput() } } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
                if ( success ) { 
                    form.setFieldsValue( { prerequisiteSet: response.data.Result } );
                    form.setFieldsValue( { 
                        prerequistieIds: response.data.Result.prerequisites.map( i => i.objectId ) 
                    } );
                    form.setFieldsValue( { 
                        prerequistieAll: response.data.Result.operator === 'AND' ? true : false 
                    } );
                    form.setFieldsValue( { prerequisiteAllModified: false } );
                    form.setFieldsValue( { prerequistiesAreModified: false } );
                    formPrerequisitesRef.current.reset(); 

                    setIsModified( false );
                    setIsSaving( true );
                } else {
                    console.error( 'Error creating prerequisite!' ); 
                    message.error( 'Error saving prerequisite!' ); 
                    hasError = true;
                    setIsSaving( false ); 
                }
            } catch ( e ) {  
                message.error( 'Error saving prerequisite!' ); 
                console.error( e );
                hasError = true;
                setIsSaving( false ); 
            }
        } 

        /**************************************
         * Delete the prerequisiteSet if a 
         * previous one exists and there are
         * now no prerequisiteIds
         **************************************/

        if ( prerequisiteSet && prerequisiteIds.length === 0 ) {
            try {
                const response = await deletePrerequisite( { variables: { input: { id: prerequisiteSet.id } } } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
                if ( success ) {
                    form.setFieldsValue( { prerequisiteSet: null } );
                    form.setFieldsValue( { prerequistieIds: [] } );
                    form.setFieldsValue( { prerequistieAll: true } );
                    form.setFieldsValue( { prerequisiteAllModified: false } );
                    form.setFieldsValue( { prerequistiesAreModified: false } );
                    formPrerequisitesRef.current.reset(); 

                    setIsModified( false );
                    setIsSaving( true );
                } else {
                    console.error( 'Error deleting prerequisite!' ); 
                    message.error( 'Error saving prerequisite!' ); 
                    hasError = true;
                    setIsSaving( false ); 
                }
            } catch ( e ) {  
                console.error( e );
                message.error( 'Error saving prerequisite!' ); 
                hasError = true;
                setIsSaving( false ); 
            }
        } 
        
        /**************************************
         * Mutate a prerequisiteSet if a
         * previous one exists and there are
         * modificatons
         **************************************/

        if ( prerequisiteSet && prerequisiteIds.length > 0 ) {
            const input = getMutationInput();
        
            try {
                input.id = prerequisiteSet.id;
                
                const response = await patchPrerequisite( { variables: { input } } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
                if ( success ) {
                    form.setFieldsValue( { prerequisiteSet: response.data.Result } );
                    form.setFieldsValue( { 
                        prerequistieIds: response.data.Result.prerequisites.map( i => i.objectId ) 
                    } );
                    form.setFieldsValue( { 
                        prerequistieAll: response.data.Result.operator === 'AND' ? true : false 
                    } );
                    form.setFieldsValue( { prerequisiteAllModified: false } );
                    form.setFieldsValue( { prerequistiesAreModified: false } );
                    formPrerequisitesRef.current.reset(); 

                    setIsModified( false );
                    setIsSaving( true );
                } else {
                    console.error( 'Error patching prerequisite!' ); 
                    message.error( 'Error saving prerequisite!' ); 
                    setIsSaving( false ); 
                    hasError = true;
                }
            } catch ( e ) {  
                console.error( e );
                message.error( 'Error saving prerequisite!' ); 
                hasError = true;
                setIsSaving( false ); 
            }
        }

        if ( !hasError ) {
            message.success( 'Course saved.' );
        }

        setIsSaving( false );
        isSavingCallback( false );
    };

    const handleReset = () => {
        formPrerequisitesRef.current.reset();
        setOpenConfirm( false ); 
        isSavingCallback( false );
    };

    const handleModifiedChange = ( value ) => {
        setIsModified( value );
    };

    return (
        <div className="prerequisites-tab tab-main-columns">
            <div className="left">
                <Spin spinning={ isSaving }>
                    <FormPrerequisites 
                        form={ form }
                        type={ type }
                        ref={ formPrerequisitesRef }
                        modifiedCallback={ handleModifiedChange } 
                    />
                </Spin>
            </div>

            <div className="right">
                <FormSaveCancelToolbar
                    form={ form } 
                    open={ openConfirm }
                    saving={ isSaving }
                    modified={ isModified }
                    saveCallback={ handleSave }
                    cancelCallback={ handleReset }
                />
            </div>
        </div>
    );
};

export default TabPrerequisites;
